// Cutover step 10 (go-live runbook; rehearsal part 2, db/054): the Ledger put next to Sage, line by line, so the
// differences are listed rather than found by reading two reports side by side.
import { useState } from 'react';
import { Banner, Button, Field, formatMoney, Pill } from '@elysian/ui';
import type { ImportBatch, SageCompare, SageCompareAged, SageCompareRow } from '../../data/source';
import { useSession } from '../../state/session';
import { longDate } from '../invoices/shared';
import { readOpenItems } from './importFile';

const readText = (f: File) => (typeof f.text === 'function' ? f.text()
  : new Promise<string>((ok, no) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => no(r.error); r.readAsText(f); }));

type Aged = { file: string; rows: SageCompareRow[] };
const count = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
/** Debit positive on the trial balance: shown as Dr or Cr, as Sage shows it. */
const drCr = (v: string) => (Number(v) === 0 ? '–' : `${formatMoney(v.replace('-', ''))} ${Number(v) < 0 ? 'Cr' : 'Dr'}`);
const signed = (v: string) => (Number(v) === 0 ? '–' : formatMoney(v));

export function CompareWithSage({ batch, onClose }: { batch: ImportBatch; onClose: () => void }) {
  const { source } = useSession();
  const [debtors, setDebtors] = useState<Aged | null>(null);
  const [creditors, setCreditors] = useState<Aged | null>(null);
  const [result, setResult] = useState<SageCompare | null>(null);
  const [onlyDiff, setOnlyDiff] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function readAged(file: File, set: (a: Aged | null) => void) {
    setError(null);
    const r = readOpenItems(await readText(file));
    if (r.problem) { setError(`${file.name}: ${r.problem}`); set(null); return; }
    set({ file: file.name, rows: r.rows.map((x) => ({ type: x.type, contact_ref: x.contact_ref, contact_name: x.contact_name, outstanding: x.outstanding })) });
    setResult(null);
  }

  async function compare() {
    setBusy(true);
    setError(null);
    try {
      setResult(await source.compareWithSage(batch.id, { ...(debtors && { debtors: debtors.rows }), ...(creditors && { creditors: creditors.rows }) }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const tb = result?.trial_balance;
  const differences = result ? (tb!.different + (result.debtors?.different ?? 0) + (result.creditors?.different ?? 0)) : 0;

  return (
    <section className="el-compare" aria-labelledby={`cmp-${batch.id}`}>
      <div className="el-spread">
        <h3 id={`cmp-${batch.id}`} className="el-h3">Compare with Sage</h3>
        <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
      </div>
      <p className="el-hint" style={{ marginTop: 4 }}>
        The trial balance you imported, as at {batch.as_at ? longDate(batch.as_at) : 'the migration date'}, against the Ledger’s opening balances,
        account by account. Add Sage’s aged debtors and creditors (the same Detailed exports as step 9) to compare each customer and supplier too.
      </p>
      <div className="el-form-grid" style={{ marginTop: 8 }}>
        <Field id={`cmp-ad-${batch.id}`} label="Aged debtors CSV (optional)" hint={debtors ? `${debtors.file}: ${count(debtors.rows.length, 'line')}` : undefined}>
          <input id={`cmp-ad-${batch.id}`} type="file" accept=".csv,text/csv" className="el-input" style={{ paddingTop: 10 }} disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void readAged(f, setDebtors); e.target.value = ''; }} />
        </Field>
        <Field id={`cmp-ac-${batch.id}`} label="Aged creditors CSV (optional)" hint={creditors ? `${creditors.file}: ${count(creditors.rows.length, 'line')}` : undefined}>
          <input id={`cmp-ac-${batch.id}`} type="file" accept=".csv,text/csv" className="el-input" style={{ paddingTop: 10 }} disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void readAged(f, setCreditors); e.target.value = ''; }} />
        </Field>
      </div>
      <div className="el-row" style={{ gap: 12, marginTop: 10, alignItems: 'center' }}>
        <Button variant="primary" disabled={busy} onClick={() => void compare()}>{result ? 'Compare again' : 'Compare'}</Button>
        {result && <label className="el-check" style={{ margin: 0 }}><input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} /> Only the differences</label>}
      </div>
      {error && <div className="el-error-box" role="alert" style={{ marginTop: 12 }}>{error}</div>}

      {result && tb && (
        <div className="el-stack" style={{ marginTop: 14, gap: 16 }}>
          {result.all_same
            ? <Banner tone="info">The Ledger agrees with Sage: {count(tb.lines.length, 'account')}{result.debtors ? `, ${count(result.debtors.lines.length, 'customer')}` : ''}{result.creditors ? `, ${count(result.creditors.lines.length, 'supplier')}` : ''}, all the same.</Banner>
            : <Banner>{differences ? `${count(differences, 'difference')} to look at.` : 'Every line agrees, but a Sage report doesn’t add up to its own trial balance.'} Put each right (or note why) before going live.</Banner>}

          <div>
            <h4 className="el-h4">Trial balance <span className="el-hint">· {tb.different ? `${tb.different} different, ` : ''}{tb.same} the same</span></h4>
            {result.carries_profit && result.carried_to && <p className="el-hint">At a year end: Sage’s income and costs are one line, on {result.carried_to}.</p>}
            <div className="el-table-wrap">
              <table className="el-table" aria-label="Trial balance compared with Sage">
                <thead><tr><th scope="col">Account</th><th scope="col">From Sage</th><th scope="col" className="el-num">Sage</th>
                  <th scope="col" className="el-num">Ledger</th><th scope="col" className="el-num">Difference</th></tr></thead>
                <tbody>
                  {tb.lines.filter((l) => !onlyDiff || !l.same).map((l) => (
                    <tr key={l.code} data-diff={!l.same || undefined}>
                      <td><strong>{l.code}</strong> {l.name}</td>
                      <td className="el-hint">{l.sage_codes.length ? l.sage_codes.map((c) => c.code).join(', ') : 'Not in Sage'}</td>
                      <td className="el-num">{drCr(l.sage)}</td>
                      <td className="el-num">{drCr(l.ledger)}</td>
                      <td className="el-num">{l.same ? <Pill tone="ok">Same</Pill> : <strong>{signed(l.difference)}</strong>}</td>
                    </tr>
                  ))}
                  {onlyDiff && tb.different === 0 && <tr><td colSpan={5} className="el-muted">No differences.</td></tr>}
                </tbody>
                <tfoot><tr><th scope="row" colSpan={2}>Totals (debit / credit)</th>
                  <td className="el-num">{formatMoney(tb.sage_debit)} / {formatMoney(tb.sage_credit)}</td>
                  <td className="el-num">{formatMoney(tb.ledger_debit)} / {formatMoney(tb.ledger_credit)}</td><td /></tr></tfoot>
              </table>
            </div>
          </div>

          {result.debtors && <AgedTable title="Aged debtors" who="Customer" aged={result.debtors} onlyDiff={onlyDiff} />}
          {result.creditors && <AgedTable title="Aged creditors" who="Supplier" aged={result.creditors} onlyDiff={onlyDiff} />}
        </div>
      )}
    </section>
  );
}

function AgedTable({ title, who, aged, onlyDiff }: { title: string; who: string; aged: SageCompareAged; onlyDiff: boolean }) {
  return (
    <div>
      <h4 className="el-h4">{title} <span className="el-hint">· {aged.different ? `${aged.different} different, ` : ''}{aged.same} the same</span></h4>
      {!aged.report_matches_trial_balance && (
        <p className="el-hint" role="note">Sage’s report adds up to {formatMoney(aged.sage_total)}, but its trial balance says {formatMoney(aged.sage_trial_balance)}:
          check both were run at the same date.</p>
      )}
      <div className="el-table-wrap">
        <table className="el-table" aria-label={`${title} compared with Sage`}>
          <thead><tr><th scope="col">{who}</th><th scope="col" className="el-num">Sage</th><th scope="col" className="el-num">Ledger</th><th scope="col" className="el-num">Difference</th></tr></thead>
          <tbody>
            {aged.lines.filter((l) => !onlyDiff || !l.same).map((l, i) => (
              <tr key={l.contact_id ?? `x${i}`} data-diff={!l.same || undefined}>
                <td>{l.name}{l.ref && <span className="el-hint"> · {l.ref}</span>}{!l.in_ledger && <div className="el-hint">Not in the Ledger: import them, then their unpaid items</div>}</td>
                <td className="el-num">{signed(l.sage)}</td>
                <td className="el-num">{signed(l.ledger)}</td>
                <td className="el-num">{l.same ? <Pill tone="ok">Same</Pill> : <strong>{signed(l.difference)}</strong>}</td>
              </tr>
            ))}
            {onlyDiff && aged.different === 0 && <tr><td colSpan={4} className="el-muted">No differences.</td></tr>}
          </tbody>
          <tfoot><tr><th scope="row">Total</th><td className="el-num">{formatMoney(aged.sage_total)}</td><td className="el-num">{formatMoney(aged.ledger_total)}</td><td /></tr></tfoot>
        </table>
      </div>
    </div>
  );
}
